import clsx from "clsx";
import { motion } from "framer-motion";
import { XIcon } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";

import Button from "~/components/Button";
import LayoutCard from "~/components/LayoutCard";
import {
	useStripeSubscription,
	useSubscriptionDB,
	useUserAction,
} from "~/hooks";

interface Props {
	isModal?: boolean;
}

const BecomeAMemberResume = ({ isModal }: Props) => {
	const { resetUserAction } = useUserAction();

	const { refreshSubscriptionDB } = useSubscriptionDB();
	const { renewStripeSubscription, subscription } = useStripeSubscription();

	const [isLoading, setIsLoading] = useState(false);

	const handleRenewSubscription = async () => {
		try {
			setIsLoading(true);
			await toast.promise(
				(async () => {
					await renewStripeSubscription();
				})(),
				{
					loading: `Resuming subscription...`,
					success: `Subscription resumed!`,
					error: `Failed to resume subscription`,
				},
			);

			setIsLoading(false);

			resetUserAction();
			refreshSubscriptionDB();
		} catch (_error) {
			setIsLoading(false);
		}
	};

	return (
		<>
			{isModal && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1, transition: { duration: 0.3 } }}
					exit={{ opacity: 0, transition: { duration: 0.2 } }}
					onClick={resetUserAction}
					className="fixed inset-0 z-50 bg-black/40 dark:bg-black/50"
					aria-hidden="true"
				/>
			)}

			<LayoutCard
				hasEnterExitAnimations={isModal}
				className={clsx({
					"fixed inset-0 z-50 mt-auto h-min max-h-[90dvh] shrink-0 overflow-scroll rounded-b-none sm:m-auto sm:max-h-[95vh] sm:max-w-md sm:rounded-xl":
						isModal,
				})}
				title={"Resume Membership"}
				subtitle="Resume membership instantly for access."
			>
				{isModal && (
					<button
						onClick={resetUserAction}
						className="absolute top-4 right-0 mr-3 cursor-pointer rounded-full bg-slate-100 p-1 transition-all hover:brightness-95 sm:-right-1 sm:mr-5 sm:block dark:bg-slate-700"
					>
						<XIcon className="h-5 phablet:h-6 phablet:w-6 w-5" />
					</button>
				)}

				<div className="flex flex-col gap-6" id="plans">
					<p className="rounded-xl border border-orange-600/20 bg-orange-50 px-3 py-2 font-medium text-orange-900 text-sm dark:border-orange-300/40 dark:bg-orange-300/20 dark:text-orange-100">
						Your membership is currently paused until{" "}
						<span className="font-semibold text-orange-800 dark:text-orange-200">
							{new Date(
								subscription?.pause_collection?.resumes_at! * 1000,
							).toDateString()}
						</span>
						, but you can reactivate it instantly and regain full access to all
						your benefits. Click below
					</p>

					<p className="text-slate-500 text-sm dark:text-slate-200">
						Note: You will be charged immediately upon reactivation.
					</p>

					<Button disabled={isLoading} onClick={handleRenewSubscription}>
						Resume Now
					</Button>
				</div>
			</LayoutCard>
		</>
	);
};

export default BecomeAMemberResume;
